import { useCallback, useRef, useState } from 'react';

import { ActionText } from '@components/action-text';
import { Badge } from '@components/badge';
import { useLocalStorageState } from '@components/component-helpers';
import { ColumnSettings, ColumnSort, Columns, DataGrid } from '@components/data-grid';
import { RowAction } from '@components/data-grid/data-grid-types';
import { EmptySearchState } from '@components/empty-search-state';
import { useDeleteBlockedModal, useDeleteModal } from '@components/generic-modals';
import { IconCopy } from '@components/icons/copy';
import { IconEdit } from '@components/icons/edit';
import { IconIndicator } from '@components/icons/indicator';
import { IconTrash } from '@components/icons/trash';
import { Spinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import {
  useCompanyTravellerAttributeTemplates,
  useDeleteTravellerAttributeTemplate,
  useDuplicateTravellerAttributeTemplate,
  useUpdateTravellerAttributeTemplate,
} from 'api/attributes/hooks';
import {
  TravellerAttributeTemplate,
  TravellerAttributeTemplateData,
  loadCompanyTravellerAttributeTemplate,
} from 'api/attributes/requests';
import { AttributeIcon } from 'ui/authenticated/settings/pages/attributes/common';
import { useAddAttributeSidebar } from 'ui/authenticated/settings/pages/attributes/sidebars/form';

const mapKeyToSortKey: Partial<Record<keyof TravellerAttributeTemplate, string>> = {
  templateTitle: 'title',
  attributeType: 'attribute_type',
  createdByName: 'created_by__name',
  dateUpdated: 'date_updated',
};

export const TravellerAttributes = ({ searchStr }: { searchStr: string }) => {
  return (
    <PageContent noPadding>
      <TravellerAttributeList searchStr={searchStr} />
    </PageContent>
  );
};

const useDeleteAttributeModal = () => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();
  const showCannotDeleteModal = useDeleteBlockedModal();
  const removeMutation = useDeleteTravellerAttributeTemplate();

  return async (attribute: TravellerAttributeTemplate) => {
    const attributeTemplateDetails = await loadCompanyTravellerAttributeTemplate(attribute.shortId);
    const isDeleteBlocked = attributeTemplateDetails?.isFieldMapped;
    if (!isDeleteBlocked) {
      showDeleteModal({
        title: t('deleteAttribute'),
        description: t('deleteAttributeDescription'),
        onConfirm: () => {
          removeMutation.mutateAsync(attribute.shortId);
        },
      });
    } else {
      showCannotDeleteModal({ description: t('cannotDelete') });
    }
  };
};

const TravellerAttributeList = ({ searchStr }: { searchStr: string }) => {
  const { t } = useScopedTranslate(locale);

  const selectedAttributeRef = useRef<string | null>(null);

  const showAddAttributeSidebar = useAddAttributeSidebar();
  const showDeleteModal = useDeleteAttributeModal();
  const updateMutation = useUpdateTravellerAttributeTemplate();
  const duplicateMutation = useDuplicateTravellerAttributeTemplate();
  const [columns] = useState<Columns<TravellerAttributeTemplate>>([
    {
      label: t('attributeLabel'),
      key: 'templateTitle',
      sortable: true,
      render: (attribute) => (
        <Stack space={2} orientation="horizontal" align="center">
          <ActionText
            font="regular14"
            color="textPrimary"
            onClick={() => {
              const shouldAnimate = selectedAttributeRef.current !== attribute.shortId;
              selectedAttributeRef.current = attribute.shortId;
              showAddAttributeSidebar({
                isTravellerAttribute: true,
                onEdit: async (data: TravellerAttributeTemplateData) =>
                  updateMutation.mutateAsync({ data, shortId: attribute.shortId }),
                attributeId: attribute.shortId,
                shouldAnimate,
              });
            }}
          >
            {attribute.templateTitle}
          </ActionText>
        </Stack>
      ),
    },
    {
      label: t('type'),
      key: 'attributeType',
      sortable: true,
      render: (attribute) => (
        <Stack space={2} orientation="horizontal" align="center">
          <AttributeIcon type={attribute.attributeType} color="elementPurple50" />
          <span>{t(attribute.attributeType)}</span>
        </Stack>
      ),
    },
    {
      label: t('createdBy'),
      key: 'createdByName',
      sortable: true,
      render: (attribute) =>
        attribute.isSystemAttribute ? <>{t('createdBySystem')}</> : <>{attribute.createdByName}</>,
    },
    {
      label: t('input'),
      key: 'input',
      sortable: false,
      render: (attribute) => (
        <span>
          {
            <Badge
              variant={attribute.isRequired ? 'green' : attribute.isActive ? 'orange' : 'neutral'}
              text={t(attribute.input)}
            />
          }
        </span>
      ),
    },
    {
      label: t('lastModified'),
      key: 'dateUpdated',
      sortable: true,
      type: 'date',
      render: (attribute) =>
        !attribute.dateUpdated || attribute.isSystemAttribute ? (
          <IconIndicator color="elementPurple60" />
        ) : (
          <span>{abbreviateDate(attribute.dateUpdated, 'en', 'date')}</span>
        ),
    },
  ]);

  const [settings, setSettings] = useLocalStorageState<ColumnSettings<TravellerAttributeTemplate>>({
    key: 'traveller-attributes-columns',
    defaultValue: {
      templateTitle: { order: 0, visible: true },
      attributeType: { order: 1, visible: true },
      input: { order: 2, visible: true },
      createdByName: { order: 2, visible: true },
      dateUpdated: { order: 3, visible: true },
    },
  });

  const [sort, setSort] = useLocalStorageState<ColumnSort<TravellerAttributeTemplate>>({
    key: 'traveller-attributes-sort',
    defaultValue: {
      key: 'dateUpdated',
      order: 'ascending',
    },
  });

  const actions = useCallback(
    (attribute: TravellerAttributeTemplate) => {
      const isDisabled = attribute.isSystemAttribute;
      const options: RowAction<TravellerAttributeTemplate>[] = [
        {
          label: t('core.edit'),
          icon: <IconEdit />,
          onSelect: () =>
            showAddAttributeSidebar({
              isTravellerAttribute: true,
              onEdit: async (data: TravellerAttributeTemplateData) =>
                updateMutation.mutateAsync({ data, shortId: attribute.shortId }),
              attributeId: attribute.shortId,
            }),
          isDisabled: () => isDisabled,
          tooltip: () => (isDisabled ? t('canNotEdit') : undefined),
        },
        {
          label: t('core.duplicate'),
          icon: <IconCopy />,
          onSelect: () => duplicateMutation.mutateAsync(attribute.shortId),
          isDisabled: () => isDisabled,
          tooltip: () => (isDisabled ? t('canNotDuplicate') : undefined),
        },
        {
          label: t('core.delete'),
          icon: <IconTrash />,
          variant: 'danger',
          isDisabled: () => (isDisabled || attribute.isFieldMapped ? true : false),
          tooltip: () =>
            isDisabled
              ? t('canNotRemove')
              : attribute.isFieldMapped
              ? t('attributeInUse')
              : undefined,
          onSelect: () => showDeleteModal(attribute),
        },
      ];
      return options;
    },
    [duplicateMutation, showAddAttributeSidebar, showDeleteModal, t, updateMutation]
  );

  const { data, isLoading } = useCompanyTravellerAttributeTemplates({
    queryData: {
      ordering: `${sort.order === 'descending' ? '-' : ''}${mapKeyToSortKey[sort.key] || sort.key}`,
      search: searchStr,
    },
    systemAttributes: true,
  });

  if (isLoading) return <Spinner />;
  if (!data || data.length === 0) {
    return <EmptySearchState />;
  }

  return (
    <DataGrid
      label={t('travellerAttributes')}
      columns={columns}
      columnSettings={settings}
      onColumnSettingsChange={setSettings}
      data={data}
      actions={actions}
      sort={sort}
      onSortChange={setSort}
    />
  );
};

const locale = {
  en: {
    travellerAttributes: 'Traveller attributes',
    attributeLabel: 'Field label',
    type: 'Type',
    input: 'Input',
    createdBy: 'Created by',
    lastModified: 'Last modified',
    addAttribute: 'Add field',
    numJourneys: 'Number of journeys',
    disable: 'Disable',
    cannotDelete: 'You cannot delete this field, since it has been used in a journey.',
    createdBySystem: 'System',
    required: 'Required',
    optional: 'Optional',
    disabled: 'Disabled',
    date: 'Select - Date',
    text: 'Text - Short',
    paragraph: 'Text - Paragraph',
    user: 'Select - User',
    phone_number: 'Telephone',
    email: 'Email',
    single_select: 'Select - Single',
    multi_select: 'Select - Multi',
    number: 'Number',
    decimal: 'Number (Decimal)',
    boolean: 'On / off',
    file: 'File',
    file_multi: 'Files',
    canNotRemove: 'Can not be removed',
    canNotEdit: 'Can not be edited',
    canNotDuplicate: 'Can not be duplicated',
    deleteAttribute: 'Delete field',
    deleteAttributeDescription: 'Are you sure you want to delete this field?',
    attributeInUse: 'Field is being used in a journey',
  },
};
